import { useCallback, useLayoutEffect, useRef, useState } from "react";

export const ACCOUNT_SELECTION_STORAGE_KEY = "savedAccount";
export const ACCOUNT_SELECTION_CHANNEL_NAME = "algodash-account-selection-v1";

type AccountSelection = string | null;

export interface BroadcastChannelAdapter {
  postMessage(data: unknown): void;
  addEventListener(
    type: "message",
    listener: (event: { data: unknown }) => void,
  ): void;
  removeEventListener(
    type: "message",
    listener: (event: { data: unknown }) => void,
  ): void;
  close(): void;
}

export interface AccountSelectionSyncEnvironment {
  storage: {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
  };
  pageShowEvents?: {
    addEventListener(
      type: "pageshow",
      listener: (event: { persisted: boolean }) => void,
    ): void;
    removeEventListener(
      type: "pageshow",
      listener: (event: { persisted: boolean }) => void,
    ): void;
  };
  createBroadcastChannel?(name: string): BroadcastChannelAdapter;
}

interface AccountSelectionMessage {
  accountId: AccountSelection;
  volatile?: true;
}

const isAccountSelection = (value: unknown): value is AccountSelection =>
  value === null || (typeof value === "string" && value.length > 0);

const isAccountSelectionMessage = (
  value: unknown,
): value is AccountSelectionMessage => {
  if (
    typeof value !== "object" ||
    value === null ||
    ![1, 2].includes(Object.keys(value).length)
  ) {
    return false;
  }
  const message = value as Record<string, unknown>;
  const keys = Object.keys(message);
  const hasValidVolatility =
    keys.length === 1 ||
    (keys.length === 2 &&
      keys.includes("volatile") &&
      message.volatile === true);
  return isAccountSelection(message.accountId) && hasValidVolatility;
};

type DurableSelectionRead =
  | { status: "unreadable" }
  | { status: "invalid" }
  | { status: "valid"; accountId: AccountSelection };

const parseStoredSelection = (value: string): DurableSelectionRead => {
  try {
    const parsed: unknown = JSON.parse(value);
    return isAccountSelection(parsed)
      ? { status: "valid", accountId: parsed }
      : { status: "invalid" };
  } catch {
    return { status: "invalid" };
  }
};

const attempt = (operation: () => void) => {
  try {
    operation();
    return true;
  } catch {
    return false;
  }
};

const attemptValue = <T>(operation: () => T): T | undefined => {
  try {
    return operation();
  } catch {
    return undefined;
  }
};

const readDurableSelection = (
  environment: AccountSelectionSyncEnvironment,
): DurableSelectionRead => {
  try {
    const value = environment.storage.getItem(ACCOUNT_SELECTION_STORAGE_KEY);
    return value === null
      ? { status: "valid", accountId: null }
      : parseStoredSelection(value);
  } catch {
    return { status: "unreadable" };
  }
};

const createChannel = (environment: AccountSelectionSyncEnvironment) =>
  attemptValue(() =>
    environment.createBroadcastChannel?.(ACCOUNT_SELECTION_CHANNEL_NAME),
  );

const postSelectionBestEffort = (
  activeChannel: BroadcastChannelAdapter | undefined,
  message: AccountSelectionMessage,
) => {
  if (activeChannel) {
    attempt(() => activeChannel.postMessage(message));
  }
};

const persistSelectionBestEffort = (
  environment: AccountSelectionSyncEnvironment,
  accountId: AccountSelection,
) =>
  attempt(() =>
    environment.storage.setItem(
      ACCOUNT_SELECTION_STORAGE_KEY,
      JSON.stringify(accountId),
    ),
  );

const disposeListenersAndChannel = ({
  pageShowEvents,
  channel,
  channelListenerRegistered,
  pageShowListenerRegistered,
  handleMessage,
  handlePageShow,
}: {
  pageShowEvents: AccountSelectionSyncEnvironment["pageShowEvents"];
  channel: BroadcastChannelAdapter | undefined;
  channelListenerRegistered: boolean;
  pageShowListenerRegistered: boolean;
  handleMessage: (event: { data: unknown }) => void;
  handlePageShow: (event: { persisted: boolean }) => void;
}) => {
  if (pageShowEvents && pageShowListenerRegistered) {
    attempt(() =>
      pageShowEvents.removeEventListener("pageshow", handlePageShow),
    );
  }
  if (channel && channelListenerRegistered) {
    attempt(() => channel.removeEventListener("message", handleMessage));
  }
  if (channel) {
    attempt(() => channel.close());
  }
};

const useAccountSelection = (
  initialEnvironment: AccountSelectionSyncEnvironment,
) => {
  const environment = useRef(initialEnvironment).current;
  const [selectedAccountId, setSelectedAccountIdState] =
    useState<AccountSelection>(null);
  const [isLoading, setIsLoading] = useState(true);
  const selectedAccountIdRef = useRef<AccountSelection>(null);
  const channelRef = useRef<BroadcastChannelAdapter>();

  const applySelection = useCallback((accountId: AccountSelection) => {
    if (selectedAccountIdRef.current === accountId) {
      return;
    }
    selectedAccountIdRef.current = accountId;
    setSelectedAccountIdState(accountId);
  }, []);

  useLayoutEffect(() => {
    let channel: BroadcastChannelAdapter | undefined;
    let channelListenerRegistered = false;
    let pageShowListenerRegistered = false;

    const handleMessage = (event: { data: unknown }) => {
      if (!isAccountSelectionMessage(event.data)) {
        return;
      }
      if (event.data.volatile) {
        applySelection(event.data.accountId);
        return;
      }
      const durable = readDurableSelection(environment);
      if (durable.status === "unreadable") {
        applySelection(event.data.accountId);
      } else if (durable.status === "valid") {
        applySelection(durable.accountId);
      }
    };
    const handlePageShow = (event: { persisted: boolean }) => {
      if (!event.persisted) {
        return;
      }
      const durable = readDurableSelection(environment);
      if (durable.status === "valid") {
        applySelection(durable.accountId);
      }
    };

    channel = createChannel(environment);
    if (channel) {
      channelRef.current = channel;
      channelListenerRegistered = attempt(() =>
        channel.addEventListener("message", handleMessage),
      );
    }

    if (environment.pageShowEvents) {
      pageShowListenerRegistered = attempt(() =>
        environment.pageShowEvents?.addEventListener(
          "pageshow",
          handlePageShow,
        ),
      );
    }

    const durable = readDurableSelection(environment);
    if (durable.status === "valid") {
      applySelection(durable.accountId);
    }
    setIsLoading(false);

    return () => {
      if (channelRef.current === channel) {
        channelRef.current = undefined;
      }
      disposeListenersAndChannel({
        pageShowEvents: environment.pageShowEvents,
        channel,
        channelListenerRegistered,
        pageShowListenerRegistered,
        handleMessage,
        handlePageShow,
      });
    };
  }, [applySelection, environment]);

  const setSelectedAccountId = useCallback(
    (accountId: AccountSelection) => {
      if (!isAccountSelection(accountId)) {
        return;
      }
      if (selectedAccountIdRef.current === accountId) {
        return;
      }

      const persisted = persistSelectionBestEffort(environment, accountId);
      applySelection(accountId);
      postSelectionBestEffort(channelRef.current, {
        accountId,
        ...(!persisted && { volatile: true as const }),
      });
    },
    [applySelection, environment],
  );

  return { selectedAccountId, setSelectedAccountId, isLoading };
};

const browserAccountSelectionEnvironment: AccountSelectionSyncEnvironment = {
  storage: {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
  },
  pageShowEvents: {
    addEventListener: (type, listener) =>
      window.addEventListener(type, listener),
    removeEventListener: (type, listener) =>
      window.removeEventListener(type, listener),
  },
  createBroadcastChannel:
    typeof BroadcastChannel === "function"
      ? (name) => {
          const channel = new BroadcastChannel(name);
          return {
            postMessage: (data) => channel.postMessage(data),
            addEventListener: (type, listener) =>
              channel.addEventListener(type, listener),
            removeEventListener: (type, listener) =>
              channel.removeEventListener(type, listener),
            close: () => channel.close(),
          };
        }
      : undefined,
};

export const useSynchronizedAccountSelection = (
  environment: AccountSelectionSyncEnvironment = browserAccountSelectionEnvironment,
) => useAccountSelection(environment);
